import { LocalDate, LocalDateTime, LocalTime } from "@js-joda/core";
import React from "react";
import { PatientTaskInstanceDetails } from "../../messages";
import { DaySlot } from "../../SelectTimeSlot";
import { AppCaregiver } from "../../system/AppState/AppCaregiverState";
import { scrollToElement } from "../../utils/scrollToElement";
import { shakeElement } from "../../utils/shakeElement";
import PatientAvatar from "../PatientAvatar/PatientAvatar";
import ScaffoldTitle from "../Scaffold/components/ScaffoldTitle";
import Scaffold from "../Scaffold/Scaffold";
import SelectTaskTimeSlotCard from "./components/SelectTaskTimeSlotCard";
import TaskInstanceChangeFooter from "./components/TaskInstanceChangeFooter";

type BaseProps = {
  caregiver: AppCaregiver;
  taskBroadcastDetails: PatientTaskInstanceDetails;
  selectedDate: LocalDate | null;
  selectedTime: LocalTime | null;
  selectedDateTime: LocalDateTime | null;
  shouldShowError: boolean;
  shouldShowTimeSlotError: boolean;
  timeSlotAppCardRef: React.RefObject<HTMLDivElement> | null;
  selectDateLabelRef: React.RefObject<HTMLDivElement> | null;
  selectTimeLabelRef: React.RefObject<HTMLDivElement> | null;
  onSelectDate: (date: LocalDate) => void;
  onSelectTime: (time: LocalTime) => void;
  onShowError: () => void;
  onClickLocation: () => void;
  onClickGoBack: () => void;
  onSchedule: (datetime: LocalDateTime) => void;
};

type Props = BaseProps &
  (
    | {
        slotsStatus: "loading";
      }
    | {
        slotsStatus: "loaded";
        slots: DaySlot[];
        message: string | null;
      }
    | {
        slotsStatus: "error";
      }
  );

export const TaskInstanceChangeScreen = (props: Props) => {
  const handleClickTakeThisTask = () => {
    if (props.selectedDate === null) {
      props.onShowError();
      if (props.selectDateLabelRef !== null && props.timeSlotAppCardRef !== null) {
        shakeElement(props.selectDateLabelRef);
        scrollToElement(props.timeSlotAppCardRef);
      }
      return;
    }

    if (props.selectedTime === null) {
      props.onShowError();
      if (props.selectTimeLabelRef !== null && props.timeSlotAppCardRef !== null) {
        shakeElement(props.selectTimeLabelRef);
        scrollToElement(props.timeSlotAppCardRef);
      }
      return;
    }

    props.onSchedule(props.selectedDate.atTime(props.selectedTime));
  };

  return (
    <Scaffold
      title={<ScaffoldTitle label={props.taskBroadcastDetails.title} />}
      endActions={
        <PatientAvatar
          key={props.taskBroadcastDetails.patientInitials}
          initials={props.taskBroadcastDetails.patientInitials}
        />
      }
      footer={
        <TaskInstanceChangeFooter
          selectedDateTime={props.selectedDateTime}
          onClickApprove={handleClickTakeThisTask}
        />
      }
      onClickGoBack={props.onClickGoBack}
      className="AvailableTaskScreen"
    >
      {(() => {
        switch (props.slotsStatus) {
          case "error":
            return <SelectTaskTimeSlotCard status="error" />;

          case "loading":
            return <SelectTaskTimeSlotCard status="loading" />;

          case "loaded":
            return (
              <SelectTaskTimeSlotCard
                status="loaded"
                slots={props.slots}
                message={props.message}
                selectedDate={props.selectedDate}
                selectedTime={props.selectedTime}
                shouldShowError={props.shouldShowError}
                timeSlotAppCardRef={props.timeSlotAppCardRef ?? undefined}
                selectDateLabelRef={props.selectDateLabelRef ?? undefined}
                selectTimeLabelRef={props.selectTimeLabelRef ?? undefined}
                onSelectDate={props.onSelectDate}
                onSelectTime={props.onSelectTime}
              />
            );
        }
      })()}
    </Scaffold>
  );
};
